import { Check, Copy, GitCompare, History, Paperclip } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Badge } from "@bigpp/ui/components/badge";
import { cn } from "@bigpp/ui/lib/utils";

import type { ChatMsg } from "../agent/useAgentRun.ts";
import { loadSnapshot, restoreSnapshot } from "../snapshots.ts";
import { Message, MessageContent, UserBubble } from "@bigpp/ui/components/message";
import { Suggestion, Suggestions } from "@bigpp/ui/components/suggestion";

import { Actions } from "./ai-elements/actions.tsx";
import { AskUserCard } from "./AskUserCard.tsx";
import { Sources } from "./ai-elements/sources.tsx";
import { ComparePanel } from "./ComparePanel.tsx";
import { ConfirmDialog } from "./ConfirmDialog.tsx";
import { Loader } from "./Loader.tsx";
import { Markdown } from "./Markdown.tsx";
import { Progress } from "./Progress.tsx";

const formatTimestamp = (ts: number): string => {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

function RevertButton({ messageId, capturedAt }: { messageId: string; capturedAt: number }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async (): Promise<void> => {
    setPending(true);
    setError(null);
    try {
      const snap = await loadSnapshot(messageId);
      if (!snap) {
        throw new Error("Snapshot is no longer available.");
      }
      await restoreSnapshot(snap.bytes);
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen(true);
        }}
        className="flex items-center gap-1 text-[10px] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors"
        title={`Snapshot captured ${formatTimestamp(capturedAt)}`}
      >
        <History className="size-2.5" />
        Revert deck to {formatTimestamp(capturedAt)}
      </button>
      <ConfirmDialog
        open={open}
        onOpenChange={(v) => {
          if (!pending) setOpen(v);
        }}
        title="Revert deck?"
        description={
          error ??
          `Replace the current deck contents with the snapshot taken at ${formatTimestamp(
            capturedAt,
          )}. Any edits since then will be lost.`
        }
        confirmLabel="Revert"
        destructive
        pending={pending}
        onConfirm={() => {
          void handleConfirm();
        }}
      />
    </>
  );
}

function CompareButton({ messageId, capturedAt }: { messageId: string; capturedAt: number }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen(true);
        }}
        className="flex items-center gap-1 text-[10px] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors"
        title={`Compare with snapshot from ${formatTimestamp(capturedAt)}`}
      >
        <GitCompare className="size-2.5" />
        Compare
      </button>
      <ComparePanel
        open={open}
        messageId={messageId}
        onClose={() => {
          setOpen(false);
        }}
      />
    </>
  );
}

export function MessageBubble({
  msg,
  onFollowUp,
  onAnswerAskUser,
}: {
  msg: ChatMsg;
  onFollowUp?: (text: string) => void;
  onAnswerAskUser?: (toolCallId: string, answer: string) => void;
}) {
  if (msg.role === "user") {
    return (
      <Message role="user" data-message-id={msg.id} className="animate-msg-in">
        <MessageContent role="user">
          {msg.attachments.length > 0 && (
            <div className="flex flex-wrap justify-end gap-1">
              {msg.attachments.map((a, i) => (
                <Badge key={i} variant="primary">
                  <Paperclip className="size-3" />
                  {a.filename}
                </Badge>
              ))}
            </div>
          )}
          {msg.content.length > 0 && <UserBubble>{msg.content}</UserBubble>}
          {msg.snapshotAt !== null && (
            <Actions align="end">
              <CompareButton messageId={msg.id} capturedAt={msg.snapshotAt} />
              <RevertButton messageId={msg.id} capturedAt={msg.snapshotAt} />
            </Actions>
          )}
        </MessageContent>
      </Message>
    );
  }

  const askUserCards = msg.toolCalls.filter(
    (tc): tc is Extract<typeof tc, { toolName: "ask_user" }> => tc.toolName === "ask_user",
  );

  return (
    <Message role="assistant" className="animate-msg-in">
      <MessageContent role="assistant">
        <Progress msg={msg} />
        {askUserCards.map((tc) => (
          <AskUserCard
            key={tc.toolCallId}
            question={tc.question}
            options={tc.options}
            answer={tc.answer}
            status={tc.status}
            onAnswer={(a) => {
              onAnswerAskUser?.(tc.toolCallId, a);
            }}
          />
        ))}
      {msg.content.length > 0 && (
        <div
          className={cn(
            "text-sm break-words text-[var(--color-foreground)]",
          )}
        >
          <Markdown>{msg.content}</Markdown>
          {!msg.finished && (
            <span
              aria-hidden
              className="ml-0.5 inline-block h-3 w-1.5 translate-y-0.5 rounded-sm bg-[var(--color-foreground)] animate-cursor-blink align-middle"
            />
          )}
        </div>
      )}
      {!msg.finished &&
        msg.content.length === 0 &&
        msg.progressParts.length === 0 && <Loader label="Thinking…" />}
      {msg.sources.length > 0 && <Sources sources={msg.sources} />}
      {msg.finished && msg.followUps.length > 0 && onFollowUp && (
        <Suggestions className="pt-1">
          {msg.followUps.map((s, i) => (
            <Suggestion
              key={i}
              onClick={() => {
                onFollowUp(s);
              }}
            >
              {s}
            </Suggestion>
          ))}
        </Suggestions>
      )}
      {msg.finished && msg.content.length > 0 && (
          <Actions>
            <CopyButton text={msg.content} />
          </Actions>
        )}
      </MessageContent>
    </Message>
  );
}


function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(
          () => {
            setCopied(true);
            window.setTimeout(() => {
              setCopied(false);
            }, 1500);
          },
          () => {
            toast.error("Could not copy to clipboard");
          },
        );
      }}
      className="flex items-center gap-1 text-[10px] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors"
      title="Copy message"
    >
      {copied ? <Check className="size-2.5" /> : <Copy className="size-2.5" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}
